import React, { useEffect, useState, useRef } from "react";
import { BsPin } from "react-icons/bs";
import TypingAnimation from "./TypingAnimation";
import { ref, onValue, off } from "firebase/database";
import { database } from "../constants/firebase";

const ChatUserList = ({ filteredUsers, navigate, togglePin, currentUser }) => {
    // Track loaded state for each user
    const [loadedMap, setLoadedMap] = useState({});
    const [typingUsers, setTypingUsers] = useState({});
    const [typingGroups, setTypingGroups] = useState({});
    const typingStateRef = useRef({}); // Track typing state locally (like in GroupList)

    // Listen for typing status in private chats
    useEffect(() => {
        if (!currentUser?.uid) return;

        const typingRef = ref(
            database,
            `Users/${currentUser.uid}/typingstatus`
        );

        const unsubscribe = onValue(typingRef, snapshot => {
            const data = snapshot.val() || {};
            const typingMap = {};

            // Process private chats (direct messages)
            Object.keys(data).forEach(chatId => {
                // Skip groups path
                if (chatId === 'groups') return;

                const users = data[chatId];
                Object.keys(users || {}).forEach(uid => {
                    const typingData = users[uid];
                    if (
                        typingData?.status &&
                        Date.now() - typingData.timestamp < 4000
                    ) {
                        typingMap[uid] = true;
                    }
                });
            });

            setTypingUsers(typingMap);
        });

        return () => off(typingRef);
    }, [currentUser?.uid]);

    // Listen for typing status in groups - FIXED VERSION using typingStateRef
    useEffect(() => {
        if (!currentUser?.uid || !filteredUsers.length) return;

        const unsubscribers = [];
        const typingState = typingStateRef.current; // Use ref to persist state between renders

        // Get all groups from filteredUsers
        const groups = filteredUsers.filter(user => user.isGroup);

        // For each group, we need to listen to typing status from ALL members
        groups.forEach(group => {
            if (!group.members) return;

            // Get all member IDs except current user
            const memberIds = Object.keys(group.members || {}).filter(
                id => id !== currentUser.uid
            );

            memberIds.forEach(memberId => {
                // Listen to each member's typing status for this group
                const typingRef = ref(
                    database,
                    `Users/${memberId}/typingstatus/groups/${group.groupId}/${memberId}`
                );

                const unsubscribe = onValue(typingRef, (snapshot) => {
                    const typingData = snapshot.val();
                    const now = Date.now();
                    const isTyping = typingData?.status && (now - typingData.timestamp < 4000);

                    // Create a unique key for this member in this group
                    const key = `${group.groupId}_${memberId}`;

                    if (isTyping) {
                        typingState[key] = true;
                    } else {
                        delete typingState[key];
                    }

                    // Count typing members for this group
                    const groupTypingCount = Object.keys(typingState)
                        .filter(k => k.startsWith(group.groupId + '_'))
                        .length;

                    setTypingGroups(prev => {
                        if (groupTypingCount === 0) {
                            const { [group.groupId]: _, ...rest } = prev;
                            return rest;
                        } else {
                            return {
                                ...prev,
                                [group.groupId]: groupTypingCount
                            };
                        }
                    });
                });

                unsubscribers.push(unsubscribe);
            });
        });

        // Cleanup function
        return () => {
            unsubscribers.forEach(unsub => unsub());
            // Clear the typing state for this group when component unmounts or dependencies change
            Object.keys(typingState).forEach(key => {
                groups.forEach(group => {
                    if (key.startsWith(group.groupId + '_')) {
                        delete typingState[key];
                    }
                });
            });
        };
    }, [currentUser?.uid, filteredUsers]);

    const handleImageLoad = (id) => {
        setLoadedMap((prev) => ({ ...prev, [id]: true }));
    };

    return (
        <>
            {filteredUsers.map((user, idx) => {
                const imageId = user.groupId || user.uid;
                const imageLoaded = loadedMap[imageId] || false;

                // Check if this is a group or individual user
                const isGroup = user.isGroup;
                const itemId = isGroup ? user.groupId : user.uid;

                // Get typing status
                let showTyping = false;
                let typingText = "";

                if (isGroup) {
                    // For groups, check if anyone is typing in this group
                    const typingCount = typingGroups[itemId];
                    if (typingCount) {
                        showTyping = true;
                        typingText = typingCount === 1
                            ? "Someone is typing"
                            : `${typingCount} people are typing`;
                    }
                } else {
                    // For individual users, check if they are typing
                    if (typingUsers[itemId]) {
                        showTyping = true;
                        typingText = "Typing";
                    }
                }

                return (
                    <div
                        key={itemId || idx}
                        className="flex items-center justify-between py-4 border-t dark:border-[#D9D9D9]/20 border-gray-200 hover:bg-[#e3eaf3] dark:hover:bg-[#D9D9D9]/10 px-2 hover:cursor-pointer"
                        onClick={() => navigate(user)}
                    >
                        <div className="flex items-center gap-4 min-w-0">
                            {/* Profile Image with shimmer */}
                            <div className="relative w-14 h-14 md:w-14 md:h-14 mx-auto flex-shrink-0 rounded-full">
                                {!imageLoaded && (
                                    <div className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse rounded-full" />
                                )}

                                <img
                                    src={
                                        isGroup
                                            ? (user?.photo || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346")
                                            : (user?.profilePic || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef")
                                    }
                                    alt={user.name || "User"}
                                    className={`w-full h-full rounded-full object-cover transition-opacity duration-300 ${!imageLoaded ? "opacity-0" : "opacity-100"
                                        }`}
                                    onLoad={() => handleImageLoad(imageId)}
                                    onError={() => handleImageLoad(imageId)}
                                />

                                {!isGroup && user.online && (
                                    <span className="absolute top-0 right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-[#181A20]" />
                                )}
                            </div>

                            {/* User Info */}
                            <div className="flex flex-col min-w-0">
                                <p
                                    className={`flex items-center gap-2 font-semibold text-base truncate ${!isGroup && user.role === "Host"
                                            ? "dark:glow-text-dark glow-text-light"
                                            : "text-black dark:text-white"
                                        }`}
                                >
                                    {user.name || "User"}
                                    {!isGroup && user?.isTeamUser && (
                                        <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#327691">
                                            <path d="M10.5213 2.62368C11.3147 1.75255 12.6853 1.75255 13.4787 2.62368L14.4989 3.74391C14.8998 4.18418 15.4761 4.42288 16.071 4.39508L17.5845 4.32435C18.7614 4.26934 19.7307 5.23857 19.6757 6.41554L19.6049 7.92905C19.5771 8.52388 19.8158 9.10016 20.2561 9.50111L21.3763 10.5213C22.2475 11.3147 22.2475 12.6853 21.3763 13.4787L20.2561 14.4989C19.8158 14.8998 19.5771 15.4761 19.6049 16.071L19.6757 17.5845C19.7307 18.7614 18.7614 19.7307 17.5845 19.6757L16.071 19.6049C15.4761 19.5771 14.8998 19.8158 14.4989 20.2561L13.4787 21.3763C12.6853 22.2475 11.3147 22.2475 10.5213 21.3763L9.50111 20.2561C9.10016 19.8158 8.52388 19.5771 7.92905 19.6049L6.41553 19.6757C5.23857 19.7307 4.26934 18.7614 4.32435 17.5845L4.39508 16.071C4.42288 15.4761 4.18418 14.8998 3.74391 14.4989L2.62368 13.4787C1.75255 12.6853 1.75255 11.3147 2.62368 10.5213L3.74391 9.50111C4.18418 9.10016 4.42288 8.52388 4.39508 7.92905L4.32435 6.41553C4.26934 5.23857 5.23857 4.26934 6.41554 4.32435L7.92905 4.39508C8.52388 4.42288 9.10016 4.18418 9.50111 3.74391L10.5213 2.62368Z" stroke="#327691" strokeWidth="1.5"></path>
                                            <path d="M9 12L11 14L15 10" stroke="#327691" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                        </svg>
                                    )}
                                    {!isGroup && user.isRequest && (
                                        <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                                            Request
                                        </span>
                                    )}
                                </p>

                                {/* Message or Typing Indicator */}
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {showTyping ? (
                                        <TypingAnimation
                                            className="text-primary text-sm"
                                            text={typingText}
                                        />
                                    ) : (
                                        user?.message || "No message"
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Right Side */}
                        <div className="flex flex-col items-end gap-2 min-w-[80px]">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                {user.displayTime || ""}
                            </div>
                            <div className="flex items-center gap-2">
                                <BsPin
                                    className={`cursor-pointer ${user.pinned ? "text-[#327691]" : "text-gray-400"
                                        }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        togglePin(itemId, isGroup);
                                    }}
                                    size={20}
                                />
                                {user.unread > 0 && !showTyping && (
                                    <span className="bg-[#327691] text-white rounded-full px-2 py-0.5 text-xs font-bold">
                                        {user.unread}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default ChatUserList;